import {forwardRef} from 'react'
import {type TextProps} from 'react-native'
import Svg, {
  Defs,
  LinearGradient,
  Path,
  type PathProps,
  Stop,
  type SvgProps,
} from 'react-native-svg'
import {Image} from 'expo-image'

import {useKawaiiMode} from '#/state/preferences/kawaii'
import {flatten, useTheme} from '#/alf'

const ratio = 128462 / 127976

type Props = {
  fill?: PathProps['fill']
  style?: TextProps['style']
} & Omit<SvgProps, 'style'>

export const Logo = forwardRef(function LogoImpl(props: Props, ref) {
  const t = useTheme()
  const {fill, ...rest} = props
  const gradient = fill === 'sky'
  const styles = flatten(props.style)
  const _fill = gradient
    ? 'url(#sky)'
    : fill || styles?.color || t.palette.primary_500
  // @ts-ignore it's fiiiiine
  const size = parseInt(rest.width || 32, 10)

  const isKawaii = useKawaiiMode()

  if (isKawaii) {
    return (
      <Image
        source={
          size > 100
            ? require('../../../assets/kawaii.png')
            : require('../../../assets/kawaii_smol.png')
        }
        accessibilityLabel="Bluesky"
        accessibilityHint=""
        accessibilityIgnoresInvertColors
        style={[{height: size, aspectRatio: 1.4}]}
      />
    )
  }

  return (
    <Svg
      fill="none"
      // @ts-ignore it's fiiiiine
      ref={ref}
      viewBox="0 0 127976 128462"
      {...rest}
      style={[{width: size, height: size * ratio}, styles]}>
      {gradient && (
        <Defs>
          <LinearGradient id="sky" x1="0" y1="0" x2="0" y2="1">
            <Stop offset="0" stopColor="#06654A" stopOpacity="1" />
            <Stop offset="1" stopColor="#59F8CB" stopOpacity="1" />
          </LinearGradient>
        </Defs>
      )}

      {/*
        Bluesky butterfly mark, replaced by the astrabun.com rabbit below.
        Source: assets/icons/rabbit.svg
        <Path
          fill={_fill}
          d="M13.873 3.805C21.21 9.332 29.103 20.537 32 26.55v15.882c0-.338-.13.044-.41.867-1.512 4.456-7.418 21.847-20.923 7.944-7.111-7.32-3.819-14.64 9.125-16.85-7.405 1.264-15.73-.825-18.014-9.015C1.12 23.022 0 8.51 0 6.55 0-3.268 8.579-.182 13.873 3.805ZM50.127 3.805C42.79 9.332 34.897 20.537 32 26.55v15.882c0-.338.13.044.41.867 1.512 4.456 7.418 21.847 20.923 7.944 7.111-7.32 3.819-14.64-9.125-16.85 7.405 1.264 15.73-.825 18.014-9.015C62.88 23.022 64 8.51 64 6.55c0-9.818-8.578-6.732-13.873-2.745Z"
        />
      */}
      <Path
        fill={_fill}
        d="m1891 4148 2088 1397 1826 1053c10975 2871 25113 16142 30165 26513 2503 5140 3790 8876 4551 8528 224-103 2206 158 2795-154 1995-1055 3600-4518 3714-11217 173-10199 4711-20601 14325-25429 1268-1173 3949-2684 5073-3908 1665-1169 4833-1371 6074 173 1293 1610 1269 4901 1287 7032l3 1022c1261 3214-79 12528-1265 16003-1208 3540-3089 7193-4370 8876l-1366 1562c-1903 2722-3994 3146-4916 5695-344 954-656 1949-822 2854-840 4582-1175 2950-890 4641 387 2292 1559 2823 2723 2313 12056-5287 21392-8652 32514-6610 3789 695 12133 2929 15396 4630 3928 2048 7602 5366 10921 10455 5024 7701 4340 8922 5807 17846 557 6002 1531 16400-3765 22574l-57 60c1569 1715 666 5192 583 7422-132 3574 1109 8652-3015 10618l-551 240-4025 1394c-1601 244-1972 254-3428 1007-973 504-1865 1012-2906 1555-1317 689-2942 1454-3536 1662-1790 626-4426 753-6001 585-1803-191-2104-431-3953-235-1650 176-2288 407-3576-470-1279-236-2579-1146-3254-2129-2254-3866 4975-6891 5288-9728-164 93-2322 401-2892 480-3997 554-16607 734-18211 283-373-105-918 356-1118 654-1650 2832-5607 8674-5976 11767-117 985-18 1908-702 2358-285 188-1047 448-1406 605-1455 315-2137 363-3601 326l-3740-19-6745-277-1008-377-1569-1052c-244-222-969-290-540-2529 76-396 586-1699 777-2128 1016-1674 1123-2191 2546-2948 1750-1271 2407-2256 2691-3086 699-2044 1495-4323 2031-6409-1406 447-3323 297-3888 209l-1782 2617c-342 393-1270 2079-1598 3085-167 514-172 605-324 984-214 532-92 463-345 1352-448 1574-1115 1847-2691 2148-953 359-7783 1251-8096 123l-2733 111c-1981 49-3602 78-5440-695-1783-1677 364-6003 2362-6963 791-1458 2205-1549 3506-2152l2790-994c1299-542 1253-641 1559-1882l177-3045-22-1898c-55-1927 467-1322-272-2851-448-926-764-1311-1510-1869-856-641-1546-1368-2035-2312-801-1547-2353-4319-2758-6298-113-556-217-1086-138-1660 40-290 105-604 184-933-248 248-797 377-1829 322-1863-101-3861-1084-5668-1440-4270 697-5587-3271-6089-6623-481-3215 3229-14052 3640-17588 470-4045 873-8138 3540-10877 673-691 791-903 1350-1604 592-741 1196-843 1482-2201-1410-1087-2101-2503-2547-4297-712-1262-154-1650-1682-3327-986-1083-1495-1398-2597-2354-4633-3300-7707-7032-9777-11044-2935-5689-6050-11514-8769-17275l-1975-1699c-1361-679-2329-1914-1687-3349 1589 272 431 209 1688 801z"
      />
    </Svg>
  )
})
